import {
  accentVars as themerAccentVars,
  defineAccentScale,
  type AccentScale,
  type AccentStop,
  type AccentTheme,
} from "@norkron/web-themer";

/**
 * Workspace accent palette — the 8-hue "archival" set (design-system §1.2).
 * Each workspace stains its accent onto the far-left spine, the switcher chip,
 * focus rings, and primary actions, while the neutral chrome stays constant.
 *
 * ── ⛔ THE CLAIM THAT WAS FALSE ────────────────────────────────────────────
 *
 * This file used to ship a third field per hue — `on`, the text colour worn on
 * the accent fill — and document it as "AA-contrast". Every one of the eight was
 * hand-typed, and the doc comment asserted an accessibility guarantee that
 * nothing had ever computed. MEASURED through `@norkron/web-color`, three of the
 * sixteen hue × theme cells were not merely short of AA — they were **below
 * WCAG's 3:1 large-text FLOOR**, i.e. the pairing failed for any use at all:
 *
 *   verdigris/dark  #4fb39c + #ffffff → 2.54:1
 *   terracotta/dark #e08a68 + #ffffff → 2.62:1
 *   slate-teal/dark #6ba3bd + #ffffff → 2.76:1
 *
 * All three are pale dark-theme stops that were given WHITE text. Derived
 * selection repairs all three by choosing the dark candidate instead
 * (6.76 / 6.55 / 6.22). No stop moved; only the *unmeasured assertion* did.
 *
 * ── WHAT REPLACES IT ──────────────────────────────────────────────────────
 *
 * `web-themer`'s `defineAccentScale()`. The API deliberately accepts NO `on`
 * field: it measures both candidate text tokens against each stop with
 * `contrastRatio` / `wcagGrade` / `apcaContrast` and returns the winner. An
 * accessibility claim becomes a computation, and `src/lib/accents.test.ts`
 * asserts the full sixteen-cell table so it stays one.
 *
 * ⚠️ `textTokens` is LOAD-BEARING and must stay bound to elaborate's own
 * `#231a05` (the ochre-derived near-black). Measured with `web-themer`'s default
 * dark candidate `#1f2937` instead, **6 of 16** cells miss body AA rather than 2
 * (ochre/light drops 4.72 → 4.03; cyanotype/oxblood/aubergine dark all fall to
 * ~4.3). The product token is not decoration.
 *
 * ⚠️ TWO CELLS STILL MISS BODY AA AFTER OPTIMAL DERIVATION, and they are an OPEN
 * OWNER DECISION, asserted as failures rather than tuned away:
 *
 *   prussian/dark    #5f79b8 + #ffffff → 4.27:1   (AA-large, not AA body)
 *   terracotta/light #c05e3c + #ffffff → 4.28:1   (AA-large, not AA body)
 *
 * Fine for a 3px spine, a swatch or a focus ring; ⛔ NOT for the button label or
 * the avatar initials, which are exactly what `--ela-on-accent` paints. The two
 * honest fixes are (a) move those two stops — the scale computes the nearest
 * passing same-hue stop for each, `#5874b6` (ΔL −2 → 4.58) and `#ba5b3b`
 * (ΔL −1 → 4.52) — or (b) restrict those hues to non-body-text use and enforce
 * it in CSS. ⛔ NOT a third option: `largeText: true` would make the table go
 * green while the text stayed unreadable, and widening the grade band would let
 * `web-color`'s `'AA-lg'` verdict pass through `every(r => r.grade !== 'fail')`
 * — the precise false green the lib's three-valued `grade` exists to prevent.
 *
 * The scale is therefore built with `onFail: 'report'`: it emits
 * `THEMER_ACCENT_CONTRAST_FAIL` (deduped, non-throwing) so the shortfall is on
 * the record at every boot instead of silent, and `failures` stays inspectable.
 * ⛔ Do NOT silence that by relaxing the floor.
 */

/**
 * The eight hue stops. TWO fields, never three — the on-accent text colour is
 * derived by measurement below and cannot be supplied here.
 */
const STOPS: Record<string, AccentStop> = {
  cyanotype: { light: "#2c5fa8", dark: "#5b8fd6" },
  oxblood: { light: "#9b3b2e", dark: "#d0705f" },
  verdigris: { light: "#2e7d6b", dark: "#4fb39c" },
  aubergine: { light: "#6a4a7c", dark: "#a17fb5" },
  ochre: { light: "#b7791f", dark: "#e0a94a" },
  prussian: { light: "#243b6b", dark: "#5f79b8" },
  terracotta: { light: "#c05e3c", dark: "#e08a68" },
  "slate-teal": { light: "#3a6e86", dark: "#6ba3bd" },
};

/**
 * The two text candidates the derivation chooses between.
 *
 * `light` is the light-coloured candidate worn on dark fills; `dark` is the
 * dark-coloured one worn on light fills. `#231a05` is elaborate's own
 * near-black, NOT `web-themer`'s `#1f2937` default — see the warning above for
 * the measured cost of drifting off it.
 */
export const ACCENT_TEXT_TOKENS = {
  light: "#ffffff",
  dark: "#231a05",
} as const;

/** The measured, contrast-verified scale. `audit()` is the deliverable. */
export const WORKSPACE_ACCENTS: AccentScale = defineAccentScale(STOPS, {
  minGrade: "AA",
  textTokens: ACCENT_TEXT_TOKENS,
  // See the header: two cells are a known, asserted, open owner decision. A
  // throw here would take the whole SPA down over a palette choice; a silent
  // pass would be the defect this file exists to have stopped telling.
  onFail: "report",
});

/**
 * One hue's two fills.
 *
 * ⚠️ There is deliberately no `on` here any more. The on-accent colour depends
 * on the RESOLVED THEME — five of the eight hues want light text on their light
 * stop and dark text on their dark stop — so a single theme-independent `on`
 * cannot be correct for both, and the old one was wrong for three of them. Use
 * {@link accentOn}.
 */
export interface Accent {
  /** light-theme accent fill */
  light: string;
  /** dark-theme accent fill */
  dark: string;
}

export const ACCENTS: Record<string, Accent> = STOPS;

export const DEFAULT_ACCENT = "cyanotype";

export function resolveAccent(name: string | undefined | null): Accent {
  if (name && ACCENTS[name]) return ACCENTS[name];
  return ACCENTS[DEFAULT_ACCENT];
}

/** The hue key an unknown/absent accent name falls back to. */
function resolveAccentName(name: string | undefined | null): string {
  return name && WORKSPACE_ACCENTS.has(name) ? name : DEFAULT_ACCENT;
}

/**
 * The MEASURED on-accent text colour for a hue in a theme — the winner of the
 * contrast comparison, not a typed constant.
 */
export function accentOn(
  name: string | undefined | null,
  resolved: AccentTheme,
): string {
  return WORKSPACE_ACCENTS.on(resolveAccentName(name), resolved);
}

/**
 * CSS custom properties to bind an accent onto a workspace shell root.
 *
 * The `--norkron-*` half comes straight from the scale, so it is the ecosystem's
 * token contract rather than this file's opinion — including
 * `--norkron-color-accent` emitted LITERALLY next to `--norkron-accent`. That is
 * not redundancy: `base.css` defines the alias as
 * `--norkron-color-accent: var(--norkron-accent, …)`, and a custom property
 * whose value contains `var()` is substituted for the element that DECLARES it —
 * so re-binding `--norkron-accent` on one workspace shell would move the accent
 * and leave the alias pinned to `:root`'s. Both literal is what makes a scoped
 * accent actually scoped.
 *
 * `--ela-spine` and `--ela-on-accent` are elaborate's own two names for the same
 * two values (the 3px tenant spine, and the text worn on every solid-accent
 * affordance); they are bridged here so nothing downstream has to know which
 * layer a token came from.
 */
export function accentVars(
  name: string | undefined | null,
  resolved: AccentTheme,
): Record<string, string> {
  const hue = resolveAccentName(name);
  const vars = themerAccentVars(WORKSPACE_ACCENTS, hue, resolved);
  return {
    ...vars,
    "--ela-spine": WORKSPACE_ACCENTS.stop(hue, resolved),
    "--ela-on-accent": WORKSPACE_ACCENTS.on(hue, resolved),
  };
}
